import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { MapPin, Loader2, Search } from "lucide-react";

/**
 * Address autocomplete backed by /api/admin/geocode/suggest (Nominatim).
 *
 * Props:
 * - value: current display string (controlled)
 * - onSelect({lat, lon, display_name}) — when the user picks a suggestion
 * - onChange(text) — text editing
 * - placeholder
 * - country: ISO code (default "ch")
 * - testId
 */
export default function AddressAutocomplete({ value, onChange, onSelect, placeholder, country = "ch", testId = "addr-autocomplete", disabled = false }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    const q = (value || "").trim();
    if (q.length < 3) { setItems([]); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/admin/geocode/suggest`, { params: { q, country, limit: 6 } });
        setItems(Array.isArray(data) ? data : []);
        setOpen(true);
      } catch { setItems([]); }
      finally { setLoading(false); }
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [value, country]);

  const pick = (it) => {
    setOpen(false);
    setItems([]);
    onSelect?.({ lat: it.lat, lon: it.lon, display_name: it.display_name });
  };

  return (
    <div ref={boxRef} className="relative" data-testid={testId}>
      <div className="relative">
        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none">
          <Search size={13} />
        </span>
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange?.(e.target.value)}
          onFocus={() => items.length > 0 && setOpen(true)}
          placeholder={placeholder || "Saisissez une adresse…"}
          disabled={disabled}
          data-testid={`${testId}-input`}
          className="w-full border border-zinc-900 pl-7 pr-7 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#FFC60A] disabled:bg-zinc-50"
        />
        {loading && (
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400">
            <Loader2 size={13} className="animate-spin" />
          </span>
        )}
      </div>
      {open && items.length > 0 && (
        <div className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-zinc-900 shadow-[3px_3px_0_0_#0A0A0B]" data-testid={`${testId}-suggestions`}>
          {items.map((it, i) => (
            <button
              key={`${it.display_name}-${i}`}
              type="button"
              onClick={() => pick(it)}
              data-testid={`${testId}-item-${i}`}
              className="w-full text-left px-3 py-2 text-xs hover:bg-[#FFF7D6] flex items-start gap-2 border-b border-zinc-100 last:border-b-0"
            >
              <MapPin size={11} className="text-zinc-500 mt-0.5 flex-shrink-0" />
              <span className="truncate" title={it.display_name}>{it.display_name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
